import React, { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crop, makeAspectCrop, ReactCrop } from 'react-image-crop';
import { useDispatch, useSelector } from 'react-redux';
/* eslint-disable max-len */
import { LandingPageCoverImage } from '@1po/1po-bff-fe-spec/generated/backoffice/landing_page_cover/model/LandingPageCoverImage';
import { InboxOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
  fetchLandingPageCoversRequestSaga,
  getActiveLandingPageCovers,
  getIsUploadingLandingPageCover,
  uploadLandingPageCoverRequestSaga,
} from 'domains/landingPageCover/LandingPageCover.store';
import {
  SCoverCard,
  SCoverInfo,
  SCoverRight,
  SCoverThumbnail,
  SDropZone,
  SFileName,
  SHiddenInput,
  SModalActions,
  SPageContainer,
  SPreviewImage,
} from 'pages/BackOfficePage/BackOfficeCategories/LandingPageConfig/LandingPageConfig.styled';
import { BlackButton, Divider, Flex, MarginBox, Modal, Tag, Text, WhiteButton } from 'UI';

const ALLOWED_EXTENSIONS = new Set(['webp', 'jpg', 'jpeg', 'png']);
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MIN_WIDTH = 2560;
const MIN_HEIGHT = 1440;

interface PreviewDimensions {
  width: number;
  height: number;
}

const LandingPageConfig = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const activeCovers = useSelector(getActiveLandingPageCovers);
  const isUploading = useSelector(getIsUploadingLandingPageCover);
  const activeCover = activeCovers[0];

  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [previewDimensions, setPreviewDimensions] = useState<PreviewDimensions | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [crop, setCrop] = useState<Crop>();
  const [croppedBase64, setCroppedBase64] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchLandingPageCoversRequestSaga());
  }, [dispatch]);

  const validationErrorMessage = t(
    'backoffice.landing_page_cover.validation_error',
    'Supported extension: WebP, Jpg, Png. File size max 5 MB',
  );

  const requirement1Text = t(
    'backoffice.landing_page_cover.requirement1',
    'Minimum: 2560px × 1440px – Aspect ratio: 16:9 – File size: Max 5 MB ',
  );

  const requirement2Text = t(
    'backoffice.landing_page_cover.requirement2',
    'Supported files: WebP, JPG, PNG – Resolution 72 DPI',
  );

  const handleReplace = () => {
    fileInputRef.current?.click();
  };

  const processFile = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ALLOWED_EXTENSIONS.has(extension)) {
      setValidationError(validationErrorMessage);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setValidationError(validationErrorMessage);
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => setValidationError(validationErrorMessage);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const image = new Image();
      image.onerror = () => setValidationError(validationErrorMessage);
      image.onload = () => {
        if (image.naturalWidth < MIN_WIDTH || image.naturalHeight < MIN_HEIGHT) {
          setValidationError(validationErrorMessage);
          return;
        }
        setValidationError(null);
        setPreviewFile(file);
        setPreviewDataUrl(dataUrl);
        setPreviewDimensions({ width: image.naturalWidth, height: image.naturalHeight });
        const defaultCrop = makeAspectCrop({ unit: '%', width: 100 }, 16 / 9, image.naturalWidth, image.naturalHeight);
        setCrop(defaultCrop);
        const canvas = document.createElement('canvas');
        canvas.width = MIN_WIDTH;
        canvas.height = MIN_HEIGHT;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(
          image,
          (defaultCrop.x / 100) * image.naturalWidth,
          (defaultCrop.y / 100) * image.naturalHeight,
          (defaultCrop.width / 100) * image.naturalWidth,
          (defaultCrop.height / 100) * image.naturalHeight,
          0,
          0,
          MIN_WIDTH,
          MIN_HEIGHT,
        );
        setCroppedBase64(canvas.toDataURL('image/jpeg', 0.92).split(',')[1] ?? '');
      };
      image.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }
    processFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleSaveCrop = () => {
    if (!previewDataUrl || !crop) return;
    const img = new Image();
    img.src = previewDataUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = MIN_WIDTH;
      canvas.height = MIN_HEIGHT;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(
        img,
        (crop.x / 100) * img.naturalWidth,
        (crop.y / 100) * img.naturalHeight,
        (crop.width / 100) * img.naturalWidth,
        (crop.height / 100) * img.naturalHeight,
        0,
        0,
        MIN_WIDTH,
        MIN_HEIGHT,
      );
      setCroppedBase64(canvas.toDataURL('image/jpeg', 0.92).split(',')[1] ?? '');
    };
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewDataUrl(null);
    setPreviewDimensions(null);
    setCrop(undefined);
    setCroppedBase64(null);
  };

  const handleSetAsCover = () => {
    if (!previewFile || !previewDimensions || !previewDataUrl) {
      return;
    }
    const extension = previewFile.name.split('.').pop()?.toLowerCase() ?? '';
    const base64 = croppedBase64 ?? previewDataUrl.split(',')[1] ?? '';
    const width = croppedBase64 ? MIN_WIDTH : previewDimensions.width;
    const height = croppedBase64 ? MIN_HEIGHT : previewDimensions.height;
    dispatch(
      uploadLandingPageCoverRequestSaga({
        base64,
        extension,
        name: previewFile.name,
        width,
        height,
        sizeKB: Math.ceil(previewFile.size / 1024),
      }),
    );
    closePreview();
  };

  const handleCancel = () => {
    closePreview();
    setValidationError(null);
  };

  const renderMetadata = (cover: LandingPageCoverImage) => {
    const dimensions = cover.width && cover.height ? `${cover.width}×${cover.height}px` : undefined;
    const size = cover.sizeKB ? `${cover.sizeKB} KB` : undefined;
    const uploaded = cover.uploadedAt
      ? t('backoffice.landing_page_cover.uploaded_at', 'Uploaded {{ago}}', { ago: moment(cover.uploadedAt).fromNow() })
      : undefined;
    return [dimensions, size, uploaded].filter(Boolean).join(' · ');
  };
  const LabelRequirements = () => {
    return (
      <Flex direction={'column'}>
        <Text type={'text'}>{requirement1Text}</Text>
        <Text type={'text'}>{requirement2Text}</Text>
      </Flex>
    );
  };
  return (
    <SPageContainer>
      <Text type="heading3">{t('backoffice.landing_page_cover.title', 'Change Landing cover')}</Text>
      <MarginBox mt={8} />
      <LabelRequirements />

      <SHiddenInput
        ref={fileInputRef}
        type={'file'}
        accept={'.webp,.jpg,.jpeg,.png'}
        onChange={handleFileChange}
        data-testid={'cover-file-input'}
      />

      {validationError && (
        <MarginBox mt={8}>
          <Text type="base" color="red">
            <span data-testid={'cover-validation-error'}>{validationError}</span>
          </Text>
        </MarginBox>
      )}

      {!activeCover && (
        <SDropZone
          $isDragging={isDragging}
          onClick={handleReplace}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid={'cover-drop-zone'}
        >
          <InboxOutlined style={{ fontSize: 48 }} />
          <Text type={'text'}>
            {t('backoffice.landing_page_cover.drop_zone_title', 'Click or drag file to this area to upload')}
          </Text>
          <Text type="base">
            {t('backoffice.landing_page_cover.drop_zone_subtitle', 'Support for a single or bulk upload.')}
          </Text>
        </SDropZone>
      )}

      {activeCover && (
        <SCoverCard data-testid={'active-cover-card'}>
          <Flex direction={'row'}>
            <SCoverInfo>
              <Flex align={'center'} wrap={'wrap'} gap={8} minWidth={0}>
                <SFileName type={'text_bold'}>{activeCover.fileName}</SFileName>
                <Tag color={'green'}>{t('backoffice.landing_page_cover.status_active', 'Active')}</Tag>
              </Flex>
              {activeCover.uploadedByName && (
                <Text type="base">
                  {t('backoffice.landing_page_cover.added_by', 'Added by {{name}}', {
                    name: activeCover.uploadedByName,
                  })}
                </Text>
              )}
              <Text type="base">{renderMetadata(activeCover)}</Text>
            </SCoverInfo>

            <SCoverRight>
              {activeCover.imageUrl && (
                <SCoverThumbnail
                  src={activeCover.imageUrl}
                  alt={activeCover.fileName}
                  data-testid={'active-cover-thumbnail'}
                />
              )}
            </SCoverRight>
          </Flex>
          <Divider style={{ margin: '0px 0px 10px 0px' }} />
          <Flex justify={'flex-end'}>
            <BlackButton onClick={handleReplace}>
              <span data-testid={'cover-replace-button'}>{t('backoffice.landing_page_cover.replace', 'Replace')}</span>
            </BlackButton>
          </Flex>
        </SCoverCard>
      )}

      <Modal width={1270} open={!!previewFile} footer={null} onCancel={handleCancel} closable>
        {previewFile && (
          <div data-testid={'cover-preview-modal'}>
            {previewDataUrl && crop && (
              <MarginBox mt={20} mr={10}>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentageCrop) => setCrop(percentageCrop)}
                  aspect={16 / 9}
                  onComplete={handleSaveCrop}
                >
                  <SPreviewImage src={previewDataUrl} alt={previewFile.name} />
                </ReactCrop>
              </MarginBox>
            )}
            <MarginBox mt={16} />
            <Text type="heading4">{t('backoffice.landing_page_cover.upload_new_picture', 'Upload new picture')}</Text>
            <MarginBox mt={8} />
            <LabelRequirements />
            <SModalActions>
              <WhiteButton onClick={handleCancel}>
                <span data-testid={'cover-cancel-button'}>{t('common.cancel', 'Cancel')}</span>
              </WhiteButton>
              <BlackButton onClick={handleSetAsCover} disabled={isUploading}>
                <span data-testid={'cover-set-as-cover-button'}>
                  {t('backoffice.landing_page_cover.set_as_cover', 'Set as cover')}
                </span>
              </BlackButton>
            </SModalActions>
          </div>
        )}
      </Modal>
    </SPageContainer>
  );
};

export default LandingPageConfig;
